import type {
  DocumentKnowledgeScope,
  ProductKnowledgeScopeProduct,
  ProductKnowledgeScopeSnapshot,
  ProductKnowledgeScopeMutationInput,
} from './types';

export const getScopeSpuKey = (product: ProductKnowledgeScopeProduct) =>
  `${product.spuId}|${product.platform}|${product.region}`;

export const getScopeSkuKey = (product: ProductKnowledgeScopeProduct, skuId: string) =>
  `${skuId}|${product.platform}|${product.region}`;

const splitScopeKey = (value: string) => {
  const [id, platform, region] = value.split('|');
  return id && platform && region ? { id, platform, region } : null;
};

export const documentScopesFromSnapshot = (
  snapshot: ProductKnowledgeScopeSnapshot,
  preservedScopes: DocumentKnowledgeScope[] = [],
): DocumentKnowledgeScope[] => {
  const productScopes: DocumentKnowledgeScope[] = [];
  for (const spu of snapshot.spus) {
    const product = splitScopeKey(spu.spuKey);
    if (!product) continue;

    if (!spu.coverAllSkus && spu.selectedSkuKeys.length) {
      for (const skuKey of spu.selectedSkuKeys) {
        const sku = splitScopeKey(skuKey);
        if (!sku) continue;
        productScopes.push({
          scopeType: 'SKU',
          scopeValue: sku.id,
          platform: sku.platform,
          region: sku.region,
          groupVersion: null,
        });
      }
      continue;
    }

    productScopes.push({
      scopeType: 'SPU',
      scopeValue: product.id,
      platform: product.platform,
      region: product.region,
      groupVersion: null,
    });
  }
  const groups = snapshot.expand
    ? preservedScopes.filter((scope) => scope.scopeType === 'SKU_GROUP')
    : [];

  return [...productScopes, ...groups];
};

export type ScopeMutationCommand = Pick<ProductKnowledgeScopeMutationInput, 'members'> & {
  operation: 'add' | 'remove';
};

const scopeMember = (
  memberType: 'SPU' | 'SKU',
  spuKey: string,
  skuKeys: string[],
): ProductKnowledgeScopeMutationInput['members'][number] => ({ memberType, spuKey, skuKeys });

export const buildScopeMutationCommands = (
  current: ProductKnowledgeScopeSnapshot,
  desired: ProductKnowledgeScopeSnapshot,
): ScopeMutationCommand[] => {
  const commands: ScopeMutationCommand[] = [];
  const currentBySpu = new Map(current.spus.map((spu) => [spu.spuKey, spu]));
  const desiredBySpu = new Map(desired.spus.map((spu) => [spu.spuKey, spu]));
  const spuKeys = new Set([...currentBySpu.keys(), ...desiredBySpu.keys()]);

  for (const spuKey of spuKeys) {
    const before = currentBySpu.get(spuKey);
    const after = desiredBySpu.get(spuKey);

    if (!before && after) {
      commands.push({
        operation: 'add',
        members: [scopeMember(after.coverAllSkus ? 'SPU' : 'SKU', spuKey, after.selectedSkuKeys)],
      });
      continue;
    }

    if (before && !after) {
      commands.push({
        operation: 'remove',
        members: [scopeMember(before.coverAllSkus ? 'SPU' : 'SKU', spuKey, before.selectedSkuKeys)],
      });
      continue;
    }

    if (!before || !after) continue;
    if (before.coverAllSkus !== after.coverAllSkus) {
      if (after.coverAllSkus) {
        commands.push({ operation: 'add', members: [scopeMember('SPU', spuKey, [])] });
      } else {
        commands.push({ operation: 'remove', members: [scopeMember('SPU', spuKey, [])] });
        if (after.selectedSkuKeys.length) {
          commands.push({
            operation: 'add',
            members: [scopeMember('SKU', spuKey, after.selectedSkuKeys)],
          });
        }
      }
      continue;
    }

    if (after.coverAllSkus) continue;
    const beforeKeys = new Set(before.selectedSkuKeys);
    const afterKeys = new Set(after.selectedSkuKeys);
    const added = after.selectedSkuKeys.filter((key) => !beforeKeys.has(key));
    const removed = before.selectedSkuKeys.filter((key) => !afterKeys.has(key));

    if (added.length) {
      commands.push({ operation: 'add', members: [scopeMember('SKU', spuKey, added)] });
    }
    if (removed.length) {
      commands.push({ operation: 'remove', members: [scopeMember('SKU', spuKey, removed)] });
    }
  }

  return commands;
};

// ──── 知识列表「关联商品」列汇总（对齐 perseus getKnowledgeScopeSummary）────

export interface KnowledgeScopeSummary {
  label: string;
  spuCount?: number;
  skuCount?: number;
}

type ScopeSummaryInput = {
  associatedCount?: { spuCount: number; skuCount: number };
  scope?: {
    expand?: boolean;
    spus?: Array<{ coverAllSkus?: boolean; selectedSkuKeys?: unknown[] }>;
  };
  scopes?: Array<{ scopeType?: string }>;
  skuCount?: number;
  relatedSkuCount?: number;
  relatedSkus?: unknown[];
};

export const getKnowledgeScopeSummary = (item: ScopeSummaryInput): KnowledgeScopeSummary => {
  const count = item.associatedCount;
  return {
    label: count ? '' : '—',
    spuCount: count?.spuCount,
    skuCount: count?.skuCount,
  };
};
